import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { I18nProvider } from 'react-aria-components';
import { IntlProvider } from 'react-intl';

import type LocaleCode from '../../types/LocaleCode';

type LocalizationProps = {
    switchLocale: (locale: LocaleCode) => void;
    currentLocale?: LocaleCode;
    locales: Record<string, LocaleCode>;
};

const LocalizationContext = createContext<LocalizationProps>({
    switchLocale: () => {},
    currentLocale: undefined,
    locales: {},
});

export const useLocalization = () => {
    return useContext(LocalizationContext);
};

const leaveWithoutWarning = (url: string): void => {
    window.onbeforeunload = null;
    window.location.replace(url);
};

type LocalizationProviderProps = {
    currentLocale: LocaleCode;
    locales: Record<string, LocaleCode>;
    translations: Record<string, string>;
    overrides?: Record<string, string>;
    children: ReactNode;
};

export const LocalizationProvider = ({
    currentLocale,
    translations,
    overrides,
    locales,
    children,
}: LocalizationProviderProps) => {
    const messages = useMemo(
        () => ({ ...translations, ...overrides }),
        [translations, overrides]
    );

    const providerValue = useMemo<LocalizationProps>(() => {
        const switchLocale = (newLocale: LocaleCode): void => {
            if (newLocale.code === currentLocale.code) {
                return;
            }

            const urlInNewLocale = window.location.href.replace(
                new RegExp(`/${currentLocale.urlSegment}/`),
                `/${newLocale.urlSegment}/`
            );

            leaveWithoutWarning(urlInNewLocale);
        };

        return {
            switchLocale,
            currentLocale,
            locales,
        };
    }, [currentLocale, locales]);

    return (
        <IntlProvider
            messages={messages}
            locale={currentLocale.ietfLanguageTag}
        >
            <I18nProvider locale={currentLocale.ietfLanguageTag}>
                <LocalizationContext.Provider value={providerValue}>
                    {children}
                </LocalizationContext.Provider>
            </I18nProvider>
        </IntlProvider>
    );
};
